import "./Notification.css";
import "./tailwind.css";
import "./styles.css";
import "./fonts.css";
import "./colors.css";
import "./globals.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { _queryClient } from "./lib/client";
import mixpanel from "mixpanel-browser";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import Terms from "./pages/legal/Terms";
import Privacy from "./pages/legal/Privacy";
import { Login, ResetPassword, EditProfile, ChangePassword } from "./pages";
import { FAQ, Presto } from "./views";
import ProtectedRoute from "./components/ProtectedRoute";
import VisionHolderPanel from "./views/VisionHolderPanel";
import { ForgotPassword, SignUp } from "./components/login";
import { GOOGLE_OAUTH_CLIENT_ID } from "./helpers/authentication";
import AuthenticationRoute from "./components/AuthenticationRoute";
import { device } from "./lib/breakpoints";
import { Helmet } from "react-helmet";
import RouterErrorBoundary from "./views/RouterErrorBoundary";
import Home from "./pages/home/Home";
import {
  APP_CANONICAL_ORIGIN,
  APP_META_DESCRIPTION,
  APP_OG_IMAGE_PATH,
  APP_PAGE_TITLE,
} from "./helpers/constants";
import Contact from "./views/Contact";
import Dashboard from "./pages/dashboard/Dashboard";
import FilingStartGate from "./components/filing/FilingStartGate";
import Pricing from "./pages/Pricing";
import HelpCenter from "./pages/HelpCenter";
import AccountSettings from "./pages/AccountSettings";
import SavedFilings from "./pages/SavedFilings";

mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN, {
  debug: !process.env.NODE_ENV || process.env.NODE_ENV === "development",
  track_pageview: true,
  persistence: "localStorage",
  ignore_dnt: true,
});

const router = createBrowserRouter([
  {
    element: <Presto />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: "/",
        element: <Home />,
      },
      /*
      {
        path: "/about",
        element: <About />,
      },
      */
      {
        path: "/privacy",
        element: <Privacy />,
      },
      {
        path: "/terms",
        element: <Terms />,
      },
      {
        path: "/reset-password",
        element: <ResetPassword />,
      },
      {
        path: "/terms-of-use",
        element: <Terms />,
      },
      {
        path: "/pricing",
        element: <Pricing />,
      },
      {
        path: "/help",
        element: <HelpCenter />,
      },
      {
        path: "/filing/start",
        element: <FilingStartGate />,
      },
      {
        path: "/contact",
        element: <Contact />,
      },
      {
        // This is just to check that you have completed your profile
        element: <ProtectedRoute />,
        children: [
          // { path: "/stories", element: <Storyworlds /> },
          // { path: "/stories/:storyId", element: <StoryworldDetail /> },
          /*
          {
            path: "/stories/:storyId/episode/:epNum",
            element: <ComicReader />,
          },
          */
          // { path: "/genres", element: <Genres /> },
          // { path: "/quests", element: <Quests /> },
          // { path: "/quests/:storyId", element: <StoryworldQuestPage /> },
          // QuestDetail, Formerly /stories/${storyId}/quests/${questId}
          /*
          {
            path: "/quests/:storyId/quest/:questId",
            element: <StoryworldQuest />,
          },
          {
            path: "/quests/:storyId/quest/:questId/submit",
            element: <ContentCreation />,
          },
          {
            path: "/quests/:storyId/quest/:questId/vote",
            element: <PollVote />,
          },
          */
          /*
          { path: "/profile", element: <Profile /> },
          */
          { path: "/profile/edit", element: <EditProfile /> },
          { path: "/profile/change-password", element: <ChangePassword /> },
          { path: "/saved-filings", element: <SavedFilings /> },
          { path: "/account", element: <AccountSettings /> },
          // { path: "/account/:id", element: <UserProfile /> },
          // { path: "/feed", element: <Feed /> },
          // { path: "/feed/settings", element: <FeedSettings /> },
        ],
      },
      {
        path: "/login",
        element: (
          <AuthenticationRoute>
            <Login />
          </AuthenticationRoute>
        ),
      },
      {
        path: "/signup",
        element: (
          <AuthenticationRoute>
            <SignUp />
          </AuthenticationRoute>
        ),
      },
      {
        path: "/forgot-password",
        element: (
          <AuthenticationRoute>
            <ForgotPassword />
          </AuthenticationRoute>
        ),
      },
      // Paths that require a user to be logged in
      {
        element: <ProtectedRoute requireAccount />,
        children: [{ path: "/presto", element: <Dashboard /> }],
      },
      {
        element: (
          <ProtectedRoute supportedBreakpoints={[device.tablet, device.desktop]} requireAdmin>
            <VisionHolderPanel />
          </ProtectedRoute>
        ),
        children: [
          /*
          { path: "/create", element: <Chat /> },
          { path: "/edit", element: <StoryworldSelector /> },
          { path: "/edit/promos", element: <PromoAdmin /> },
          { path: "/edit/promos/create", element: <CreatePromo /> },
          { path: "/edit/mod/comments", element: <CommentsModeration /> },
          */
        ],
      },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById("root")).render(
  <>
    <Helmet>
      <html lang="en" />
      <title>{APP_PAGE_TITLE}</title>
      <meta name="description" content={APP_META_DESCRIPTION} />
      <link rel="canonical" href={`${APP_CANONICAL_ORIGIN}/`} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SolvTax" />
      <meta property="og:url" content={`${APP_CANONICAL_ORIGIN}/`} />
      <meta property="og:title" content={APP_PAGE_TITLE} />
      <meta property="og:description" content={APP_META_DESCRIPTION} />
      <meta property="og:image" content={`${APP_CANONICAL_ORIGIN}${APP_OG_IMAGE_PATH}`} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={APP_PAGE_TITLE} />
      <meta name="twitter:description" content={APP_META_DESCRIPTION} />
      <meta name="twitter:image" content={`${APP_CANONICAL_ORIGIN}${APP_OG_IMAGE_PATH}`} />
    </Helmet>
    <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
      <QueryClientProvider client={_queryClient}>
        <RouterProvider router={router} />
        <ReactQueryDevtools buttonPosition="bottom-left" />
      </QueryClientProvider>
    </GoogleOAuthProvider>
  </>
);
